import React, { memo } from 'react';

// ============================================
// VisuallyHidden Component
// ============================================
// Content is hidden visually but remains accessible to screen readers

interface VisuallyHiddenProps {
  children: React.ReactNode;
  as?: React.ElementType;
}

export const VisuallyHidden: React.FC<VisuallyHiddenProps> = memo(({ 
  children, 
  as: Component = 'span' 
}) => {
  return (
    <Component
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: '0',
      }}
    >
      {children}
    </Component>
  );
});

VisuallyHidden.displayName = 'VisuallyHidden';

// ============================================
// SkipLink Component
// ============================================
// Allows keyboard users to skip to main content

interface SkipLinkProps {
  href?: string;
  children?: React.ReactNode;
}

export const SkipLink: React.FC<SkipLinkProps> = memo(({ 
  href = '#main-content',
  children = 'Skip to main content'
}) => {
  return (
    <a
      href={href}
      className="
        sr-only focus:not-sr-only
        focus:fixed focus:top-4 focus:left-4 focus:z-[9999]
        focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white
        focus:rounded-lg focus:shadow-lg focus:outline-none
        focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
        font-bold text-sm
      "
    >
      {children}
    </a>
  );
});

SkipLink.displayName = 'SkipLink';

// ============================================
// LiveRegion Component
// ============================================
// Announces dynamic content changes to screen readers

interface LiveRegionProps {
  children: React.ReactNode;
  'aria-live'?: 'polite' | 'assertive' | 'off';
  'aria-atomic'?: boolean;
  role?: 'status' | 'alert' | 'log';
  className?: string;
}

export const LiveRegion: React.FC<LiveRegionProps> = memo(({
  children,
  'aria-live': ariaLive = 'polite',
  'aria-atomic': ariaAtomic = true,
  role = 'status',
  className = '',
}) => {
  return (
    <div
      role={role}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
      className={className}
    >
      {children}
    </div>
  );
});

LiveRegion.displayName = 'LiveRegion';

export default VisuallyHidden;
